import { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { FiPlus, FiTrash2, FiSave, FiEdit2, FiImage } from "react-icons/fi";

const EMPTY_ROW = {
  bankName: "",
  accountHolder: "",
  accountNumber: "",
  branch: "",
  swift: "",
  notes: "",
  isActive: true,
  displayOrder: 100,
};

export default function AdminBankSettings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_ROW);
  const [heroUrl, setHeroUrl] = useState("");
  const [savingHero, setSavingHero] = useState(false);

  // live list
  useEffect(() => {
    const q = query(
      collection(db, "bank_accounts"),
      orderBy("isActive", "desc"),
      orderBy("displayOrder", "asc"),
      orderBy("bankName", "asc"),
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setRows(list);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // load portal_settings/public (future hero image, etc.)
  useEffect(() => {
    (async () => {
      const ref = doc(db, "portal_settings", "public");
      const s = await getDoc(ref);
      if (s.exists()) {
        const d = s.data();
        setHeroUrl(d.userPortalHeroUrl || "");
      }
    })();
  }, []);

  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onAdd = async () => {
    const user = auth.currentUser;
    const payload = {
      ...form,
      displayOrder: Number(form.displayOrder || 100),
      isActive: Boolean(form.isActive),
      updatedAt: serverTimestamp(),
      updatedBy: user?.email || user?.uid || "admin",
    };
    await addDoc(collection(db, "bank_accounts"), payload);
    setForm(EMPTY_ROW);
  };

  const onEdit = (row) => {
    setEditingId(row.id);
    setForm({
      bankName: row.bankName || "",
      accountHolder: row.accountHolder || "",
      accountNumber: row.accountNumber || "",
      branch: row.branch || "",
      swift: row.swift || "",
      notes: row.notes || "",
      isActive: !!row.isActive,
      displayOrder: row.displayOrder ?? 100,
    });
  };

  const onCancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_ROW);
  };

  const onSave = async () => {
    const user = auth.currentUser;
    const ref = doc(db, "bank_accounts", editingId);
    await updateDoc(ref, {
      ...form,
      displayOrder: Number(form.displayOrder || 100),
      isActive: Boolean(form.isActive),
      updatedAt: serverTimestamp(),
      updatedBy: user?.email || user?.uid || "admin",
    });
    onCancelEdit();
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this bank entry?")) return;
    await deleteDoc(doc(db, "bank_accounts", id));
  };

  const onSaveHero = async () => {
    setSavingHero(true);
    const user = auth.currentUser;
    const ref = doc(db, "portal_settings", "public");
    await setDoc(
      ref,
      {
        userPortalHeroUrl: (heroUrl || "").trim(),
        updatedAt: serverTimestamp(),
        updatedBy: user?.email || user?.uid || "admin",
      },
      { merge: true },
    );
    setSavingHero(false);
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Bank Settings</h1>
        <p className="text-sm text-gray-500">
          Manage the bank details shown in the customer portal. You can add
          multiple accounts and control visibility & order. Future fields (like
          front image) live below.
        </p>
      </div>

      {/* Future: User Portal Hero / Front Image */}
      <div className="mb-8 rounded-2xl border border-gray-200 p-4 bg-white">
        <div className="flex items-center gap-2 mb-3">
          <FiImage />
          <h2 className="font-semibold">User Portal Front Image (URL)</h2>
        </div>
        <div className="grid sm:grid-cols-[1fr_auto] gap-3">
          <input
            type="url"
            placeholder="https://your-cdn.com/hero.jpg"
            value={heroUrl}
            onChange={(e) => setHeroUrl(e.target.value)}
            className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <button
            onClick={onSaveHero}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2"
            disabled={savingHero}
          >
            <FiSave />
            {savingHero ? "Saving..." : "Save"}
          </button>
        </div>
        {heroUrl ? (
          <div className="mt-4">
            <img
              src={heroUrl}
              alt="Portal Hero Preview"
              className="rounded-xl max-h-56 object-cover w-full"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          </div>
        ) : null}
      </div>

      {/* Form */}
      <div className="mb-6 rounded-2xl border border-gray-200 p-4 bg-white">
        <h2 className="font-semibold mb-4">
          {editingId ? "Edit Bank Entry" : "Add Bank Entry"}
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <TextField
            label="Bank Name *"
            value={form.bankName}
            onChange={(v) => onChange("bankName", v)}
            placeholder="Commercial Bank"
          />
          <TextField
            label="Account Holder *"
            value={form.accountHolder}
            onChange={(v) => onChange("accountHolder", v)}
            placeholder="A. B. Perera"
          />
          <TextField
            label="Account Number *"
            value={form.accountNumber}
            onChange={(v) => onChange("accountNumber", v)}
            placeholder="0123456789"
          />
          <TextField
            label="Branch"
            value={form.branch}
            onChange={(v) => onChange("branch", v)}
            placeholder="Colombo Fort"
          />
          <TextField
            label="SWIFT (optional)"
            value={form.swift}
            onChange={(v) => onChange("swift", v)}
            placeholder="CCCCLKLX"
          />

          {/* <NumberField
            label="Display Order"
            value={form.displayOrder}
            onChange={(v) => onChange("displayOrder", v)}
          /> */}
          <ToggleField
            label="Active"
            value={form.isActive}
            onChange={(v) => onChange("isActive", v)}
          />
          <div className="md:col-span-2">
            <TextArea
              label="Notes (optional)"
              value={form.notes}
              onChange={(v) => onChange("notes", v)}
              rows={3}
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          {editingId ? (
            <>
              <button
                onClick={onSave}
                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2"
              >
                <FiSave />
                Save Changes
              </button>
              <button
                onClick={onCancelEdit}
                className="rounded-xl border px-4 py-2"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={onAdd}
              className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2"
            >
              <FiPlus />
              Add Bank
            </button>
          )}
        </div>
      </div>
      {/* <BankDetailsPanel /> */}
      {/* Table */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                {/* <Th>Order</Th> */}
                <Th>Active</Th>
                <Th>Bank</Th>
                <Th>Account Holder</Th>
                <Th>Account Number</Th>
                <Th>Branch</Th>
                <Th>SWIFT</Th>
                <Th>Notes</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={9} className="p-4 text-center text-gray-500">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-6 text-center text-gray-500">
                    No bank entries yet.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    {/* <Td>{r.displayOrder ?? 100}</Td> */}
                    <Td>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          r.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {r.isActive ? "Active" : "Hidden"}
                      </span>
                    </Td>
                    <Td className="font-medium">{r.bankName || "-"}</Td>
                    <Td>{r.accountHolder || "-"}</Td>
                    <Td className="font-mono">{r.accountNumber || "-"}</Td>
                    <Td>{r.branch || "-"}</Td>
                    <Td>{r.swift || "-"}</Td>
                    <Td
                      className="max-w-[280px] truncate"
                      title={r.notes || ""}
                    >
                      {r.notes || "—"}
                    </Td>
                    <Td className="text-right">
                      <button
                        onClick={() => onEdit(r)}
                        className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 mr-2 hover:bg-gray-50"
                      >
                        <FiEdit2 /> Edit
                      </button>
                      <button
                        onClick={() => onDelete(r.id)}
                        className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-red-600 hover:bg-red-50"
                      >
                        <FiTrash2 /> Delete
                      </button>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- UI bits ---------------- */
function Th({ children, className = "" }) {
  return (
    <th
      className={`text-left text-xs font-semibold uppercase tracking-wider p-3 ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`p-3 align-top ${className}`}>{children}</td>;
}
function TextField({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
}) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-gray-600 mb-1">{label}</div>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 3, placeholder = "" }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-gray-600 mb-1">{label}</div>
      <textarea
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </label>
  );
}
function ToggleField({ label, value, onChange }) {
  return (
    <label className="flex items-center gap-3">
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4"
      />
      <span className="text-sm">{label}</span>
    </label>
  );
}
